import React, { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import {
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  Clock,
  RefreshCw
} from "lucide-react";
import Layout from "../components/layout/Layout";
import { usePayment } from "../context/PaymentContext";
import ClientSessionBrief from "../components/ui/ClientSessionBrief";

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkPaymentStatus } = usePayment();

  const transactionId = searchParams.get("transaction_id");

  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [checkCount, setCheckCount] = useState(0);
  const [briefSaved, setBriefSaved] = useState(false);
  const maxChecks = 10;

  useEffect(() => {
    if (!transactionId) {
      setStatus("error");
      return;
    }

    let timeoutId = null;
    let mounted = true;

    const check = async () => {
      try {
        const result = await checkPaymentStatus(transactionId);

        if (!mounted) return;

        setPaymentInfo(result);

        if (result.paid || result.status === "succeeded") {
          setStatus("success");
        } else if (
          result.status === "canceled" ||
          result.status === "refunded"
        ) {
          setStatus("failed");
        } else if (checkCount < maxChecks) {
          setCheckCount((prev) => prev + 1);
          timeoutId = setTimeout(check, 3000);
        } else {
          setStatus("pending");
        }
      } catch (err) {
        if (!mounted) return;
        console.error("Payment check error:", err);
        if (checkCount < maxChecks) {
          setCheckCount((prev) => prev + 1);
          timeoutId = setTimeout(check, 3000);
        } else {
          setStatus("error");
        }
      }
    };

    check();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [transactionId]);

  const handleRetryCheck = () => {
    setCheckCount(0);
    setStatus("checking");
  };

  const bookingFromPayment = paymentInfo?.booking || null;

  return (
    <Layout>
      <div
        id="payment-result_page"
        className="min-h-screen flex items-center justify-center"
      >
        <div className="max-w-lg w-full px-4">
          {status === "checking" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-10 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <Loader2 className="w-16 h-16 text-primary-400 mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-medium text-white mb-3">
                Проверяем оплату
              </h1>
              <p className="text-lg font-extralight text-neutral-400 mb-2">
                Платёж обрабатывается, пожалуйста подождите...
              </p>
              <p className="text-lg font-extralight text-neutral-500">
                Проверка {checkCount + 1} из {maxChecks}
              </p>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="text-center p-10 rounded-md bg-neutral-900/50 border border-neutral-800">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent-500/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-accent-400" />
                </div>
                <h1 className="text-3xl font-medium text-white mb-4">
                  Оплата прошла успешно
                </h1>
                <p className="text-lg font-extralight text-neutral-400 mb-8">
                  Консультация забронирована и оплачена. Помогите эксперту подготовиться, заполнив короткий опросник ниже.
                </p>
              </div>

              {bookingFromPayment && !briefSaved && (
                <ClientSessionBrief
                  booking={{
                    id: bookingFromPayment.id || transactionId,
                    clientId: bookingFromPayment.clientId || paymentInfo?.clientId || "",
                    expertId: bookingFromPayment.expertId || paymentInfo?.expertId || ""
                  }}
                  expertSpecializations={bookingFromPayment.expertSpecializations || []}
                  onSaved={() => setBriefSaved(true)}
                />
              )}

              {briefSaved && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-md bg-accent-500/10 border border-accent-500/20 text-center"
                >
                  <div className="flex items-center justify-center gap-2 text-accent-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-lg font-extralight">Опросник сохранён. Эксперт увидит ваш запрос.</span>
                  </div>
                </motion.div>
              )}

              <div className="flex flex-col gap-3">
                <Link
                  to="/client/dashboard"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                >
                  Перейти в личный кабинет
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/experts"
                  className="px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 text-lg font-extralight transition-colors text-center"
                >
                  Вернуться к каталогу
                </Link>
              </div>
            </motion.div>
          )}

          {status === "failed" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-10 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-3xl font-medium text-white mb-4">
                Оплата не удалась
              </h1>
              <p className="text-lg font-extralight text-neutral-400 mb-8">
                Платёж был отменён или отклонён. Попробуйте снова или выберите
                другой способ оплаты.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/experts"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                >
                  Попробовать снова
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 text-lg font-extralight transition-colors"
                >
                  На главную
                </Link>
              </div>
            </motion.div>
          )}

          {status === "pending" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-10 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              <h1 className="text-3xl font-medium text-white mb-4">
                Платёж в обработке
              </h1>
              <p className="text-lg font-extralight text-neutral-400 mb-6">
                Оплата ещё обрабатывается. Обычно это занимает несколько минут.
                Статус обновится автоматически.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleRetryCheck}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                  Проверить снова
                </button>
                <Link
                  to="/client/dashboard"
                  className="px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 text-lg font-extralight transition-colors"
                >
                  Перейти в личный кабинет
                </Link>
              </div>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-10 rounded-md bg-neutral-900/50 border border-neutral-800"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-3xl font-medium text-white mb-4">
                Ошибка проверки
              </h1>
              <p className="text-lg font-extralight text-neutral-400 mb-8">
                Не удалось проверить статус платежа. Проверьте личный кабинет
                позже или свяжитесь с поддержкой.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/client/dashboard"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
                >
                  Личный кабинет
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 text-lg font-extralight transition-colors"
                >
                  На главную
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
}
